import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { logoutUser } from "../../redux/actions/userActions";
import useNavigateSearch from "../../services/useNavigateSearch";

type NavMenuAvatarProps = {
  railMode?: boolean;
};

function roleLabel(role?: string) {
  if (!role) return "";
  return role
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim();
}

export default function NavMenuAvatar({ railMode = false }: NavMenuAvatarProps) {
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const user = useSelector((state: any) => state.user?.user);
  const dispatch = useDispatch();
  const navigate = useNavigateSearch();

  const fullName = useMemo(
    () =>
      [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim() ||
      "Account",
    [user?.firstName, user?.lastName]
  );

  const initials = useMemo(() => {
    const parts = fullName.split(/\s+/).filter(Boolean);
    if (!parts.length) return "?";
    return parts
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? "")
      .join("");
  }, [fullName]);

  const primaryRole = roleLabel(user?.roles?.[0]);
  const photoUrl = user?.photo?.url;
  const menuOpen = Boolean(anchorElUser);

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const userSignOut = () => {
    handleCloseUserMenu();
    dispatch(logoutUser());
    navigate("/login");
  };

  if (!user) return null;

  const trigger = (
    <button
      type="button"
      className={`sidebar-profile__btn${menuOpen ? " is-open" : ""}`}
      onClick={handleOpenUserMenu}
      aria-label="account menu"
      aria-controls={menuOpen ? "account-menu" : undefined}
      aria-haspopup="true"
      aria-expanded={menuOpen}
    >
      <Avatar
        className="sidebar-profile__avatar"
        src={photoUrl ?? undefined}
        alt={fullName}
      >
        {initials}
      </Avatar>
      {!railMode && (
        <>
          <Box className="sidebar-profile__meta">
            <Typography className="sidebar-profile__name" noWrap>
              {fullName}
            </Typography>
            {primaryRole && (
              <Typography className="sidebar-profile__role" noWrap>
                {primaryRole}
              </Typography>
            )}
          </Box>
          <KeyboardArrowUpIcon
            className={`sidebar-profile__chevron${menuOpen ? " is-open" : ""}`}
          />
        </>
      )}
    </button>
  );

  return (
    <Box className="sidebar-profile">
      {railMode ? (
        <Tooltip title={fullName} placement="right" arrow>
          {trigger}
        </Tooltip>
      ) : (
        trigger
      )}

      <Menu
        id="account-menu"
        anchorEl={anchorElUser}
        open={menuOpen}
        onClose={handleCloseUserMenu}
        anchorOrigin={{
          vertical: "top",
          horizontal: railMode ? "right" : "left",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: railMode ? "left" : "left",
        }}
        MenuListProps={{
          className: "sidebar-profile__menu-list",
          dense: true,
        }}
        PaperProps={{
          elevation: 0,
          className: "app-topbar__menu-paper sidebar-profile__menu-paper",
        }}
      >
        <Box className="sidebar-profile__header">
          <Avatar
            className="sidebar-profile__header-avatar"
            src={photoUrl ?? undefined}
            alt={fullName}
          >
            {initials}
          </Avatar>
          <Box className="sidebar-profile__header-meta">
            <Typography className="sidebar-profile__header-name" noWrap>
              {fullName}
            </Typography>
            {user.email && (
              <Typography className="sidebar-profile__header-email" noWrap>
                {user.email}
              </Typography>
            )}
            {primaryRole && (
              <span className="sidebar-profile__role-chip">{primaryRole}</span>
            )}
          </Box>
        </Box>

        <Divider className="sidebar-profile__divider" />

        <MenuItem
          className="sidebar-profile__menu-item"
          component={Link}
          to="/settings"
          onClick={handleCloseUserMenu}
        >
          <ListItemIcon>
            <ManageAccountsOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Profile settings"
            secondary="Account details and preferences"
            primaryTypographyProps={{ fontWeight: 600 }}
            secondaryTypographyProps={{ variant: "caption" }}
          />
        </MenuItem>

        <Divider className="sidebar-profile__divider" />

        <MenuItem
          className="sidebar-profile__menu-item sidebar-profile__menu-item--danger"
          onClick={userSignOut}
        >
          <ListItemIcon>
            <LogoutOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Sign out"
            secondary="End your current session"
            primaryTypographyProps={{ fontWeight: 600 }}
            secondaryTypographyProps={{ variant: "caption" }}
          />
        </MenuItem>
      </Menu>
    </Box>
  );
}
